import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { utils, writeFile } from 'xlsx';

import { date as commonDate } from '@finley-technologies/common';

import { Icon } from 'packages/common-frontend/components/icons';

type InvoicesDownloadButtonProps = {
  getDownloadData: () => Promise<{
    dataRows: { [key: string]: any }[];
    headers: { label: string; key: string }[];
  }>;
  isLoading?: boolean;
};

export const InvoicesDownloadButton = ({
  getDownloadData,
  isLoading,
}: InvoicesDownloadButtonProps): React.ReactElement => {
  const [isFetching, setIsFetching] = useState(false);
  const todayFullDateStr = commonDate.currentDateStr();

  const handleCsvDownload = useCallback(async (): Promise<void> => {
    setIsFetching(true);
    try {
      const { dataRows, headers } = await getDownloadData();
      const headerRow = headers.map((h) => h.label).join(',');
      const csvRows = dataRows.map((row) =>
        headers
          .map((h) => {
            const value = row[h.key];
            let stringValue: string;
            if (value === null || value === undefined) {
              stringValue = '';
            } else if (typeof value === 'object') {
              stringValue = JSON.stringify(value);
            } else {
              stringValue = String(value);
            }
            if (
              stringValue.includes(',') ||
              stringValue.includes('"') ||
              stringValue.includes('\n')
            ) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(','),
      );
      const csvContent = [headerRow, ...csvRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${_.snakeCase('invoices')}-${todayFullDateStr}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsFetching(false);
    }
  }, [getDownloadData, todayFullDateStr]);

  const handleXlsxDownload = useCallback(async (): Promise<void> => {
    setIsFetching(true);
    try {
      const { dataRows } = await getDownloadData();
      const ws = utils.json_to_sheet(dataRows);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Data export');
      writeFile(wb, `invoices-${todayFullDateStr}.xlsx`);
    } finally {
      setIsFetching(false);
    }
  }, [getDownloadData, todayFullDateStr]);

  const showLoading = isLoading || isFetching;

  return (
    <Box style={{ alignSelf: 'self-end' }}>
      <Menu>
        <MenuButton
          as={Button}
          variant="secondary-muted"
          colorScheme="gray"
          width="fit-content"
          leftIcon={
            showLoading ? (
              <Spinner size="xs" />
            ) : (
              <Icon icon="arrow-down-to-bracket" />
            )
          }
          isDisabled={showLoading}
        >
          Download
        </MenuButton>
        <MenuList zIndex={100}>
          <MenuItem onClick={handleCsvDownload}>
            <Text>Download CSV</Text>
          </MenuItem>
          <MenuItem onClick={handleXlsxDownload}>
            <Text>Download XLSX</Text>
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};
